/**
 * State tax data — all 50 states + DC.
 *
 * Top-rate flat approximation for v1. Full bracket modeling exists for the
 * states most relevant to Linked Accounting LLP (Layton, UT): UT, ID, AZ,
 * CO, NM. Other states use a single top rate as a planning approximation —
 * verify exact computation before any final return work.
 *
 * Rates current as of TY2025 — update annually.
 *
 * NOTE on flat vs. graduated: states with a true flat rate (UT, CO, etc.)
 * are exact at a flat rate. States with graduated brackets that use the
 * top-rate-only fallback under-approximate tax slightly for low-bracket
 * clients. Flag in UI.
 */

// Pass-through entity tax election availability
// (states where a PTE election is on the books — the firm uses this to
// route entity-level state tax around the SALT cap)
export const PTE_AVAILABLE = new Set([
  "AL", "AR", "AZ", "CA", "CO", "CT", "GA", "HI", "ID", "IL",
  "IN", "IA", "KS", "KY", "LA", "MD", "MA", "MI", "MN", "MS",
  "MO", "MT", "NE", "NJ", "NM", "NY", "NC", "OH", "OK", "OR",
  "RI", "SC", "UT", "VA", "WV", "WI",
]);

// id => { name, topRate, kind, brackets?, stdDeduction?, hasPTE }
export const STATES = {
  AL: { name: "Alabama", topRate: 0.05, kind: "graduated", hasPTE: true },
  AK: { name: "Alaska", topRate: 0, kind: "none", hasPTE: false, note: "No state income tax" },
  AZ: {
    name: "Arizona",
    topRate: 0.025,
    kind: "flat",
    hasPTE: true,
    stdDeduction: { single: 14600, mfj: 29200 },
  },
  AR: { name: "Arkansas", topRate: 0.039, kind: "graduated", hasPTE: true },
  CA: { name: "California", topRate: 0.133, kind: "graduated", hasPTE: true, note: "Includes 1% mental health tax above $1M" },
  CO: {
    name: "Colorado",
    topRate: 0.044,
    kind: "flat",
    hasPTE: true,
    stdDeduction: { single: 0, mfj: 0 },
    note: "Flat 4.4% on federal taxable income",
  },
  CT: { name: "Connecticut", topRate: 0.0699, kind: "graduated", hasPTE: true },
  DE: { name: "Delaware", topRate: 0.066, kind: "graduated", hasPTE: false },
  FL: { name: "Florida", topRate: 0, kind: "none", hasPTE: false, note: "No state income tax" },
  GA: { name: "Georgia", topRate: 0.0539, kind: "flat", hasPTE: true },
  HI: { name: "Hawaii", topRate: 0.11, kind: "graduated", hasPTE: true },
  ID: {
    name: "Idaho",
    topRate: 0.0595,
    kind: "flat",
    hasPTE: true,
    stdDeduction: { single: 15000, mfj: 30000 },
    note: "Flat 5.695% (2025); conforms to federal taxable income with adjustments",
  },
  IL: { name: "Illinois", topRate: 0.0495, kind: "flat", hasPTE: true },
  IN: { name: "Indiana", topRate: 0.0305, kind: "flat", hasPTE: true },
  IA: { name: "Iowa", topRate: 0.038, kind: "flat", hasPTE: true, note: "Flat as of 2025" },
  KS: { name: "Kansas", topRate: 0.057, kind: "graduated", hasPTE: true },
  KY: { name: "Kentucky", topRate: 0.04, kind: "flat", hasPTE: true },
  LA: { name: "Louisiana", topRate: 0.0425, kind: "flat", hasPTE: true, note: "Flat 4.25% as of 2025" },
  ME: { name: "Maine", topRate: 0.0715, kind: "graduated", hasPTE: false },
  MD: { name: "Maryland", topRate: 0.0575, kind: "graduated", hasPTE: true, note: "Plus county tax 2.25–3.2%" },
  MA: { name: "Massachusetts", topRate: 0.09, kind: "graduated", hasPTE: true, note: "5% base + 4% surtax on income >$1M" },
  MI: { name: "Michigan", topRate: 0.0425, kind: "flat", hasPTE: true },
  MN: { name: "Minnesota", topRate: 0.0985, kind: "graduated", hasPTE: true },
  MS: { name: "Mississippi", topRate: 0.044, kind: "flat", hasPTE: true, note: "Flat 4.4% as of 2025" },
  MO: { name: "Missouri", topRate: 0.0470, kind: "graduated", hasPTE: true },
  MT: { name: "Montana", topRate: 0.0575, kind: "graduated", hasPTE: true, note: "Restructured to two-bracket 4.7% / 5.9% in 2024" },
  NE: { name: "Nebraska", topRate: 0.052, kind: "graduated", hasPTE: true },
  NV: { name: "Nevada", topRate: 0, kind: "none", hasPTE: false, note: "No state income tax" },
  NH: { name: "New Hampshire", topRate: 0, kind: "none", hasPTE: false, note: "No tax on wage income (interest/dividends tax repealed 2025)" },
  NJ: { name: "New Jersey", topRate: 0.1075, kind: "graduated", hasPTE: true },
  NM: {
    name: "New Mexico",
    topRate: 0.059,
    kind: "graduated",
    hasPTE: true,
    stdDeduction: { single: 15000, mfj: 30000 },
    note: "Graduated brackets; uses federal AGI as starting point",
  },
  NY: { name: "New York", topRate: 0.109, kind: "graduated", hasPTE: true, note: "Plus NYC residency 3.876%" },
  NC: { name: "North Carolina", topRate: 0.0425, kind: "flat", hasPTE: true },
  ND: { name: "North Dakota", topRate: 0.025, kind: "graduated", hasPTE: false },
  OH: { name: "Ohio", topRate: 0.035, kind: "graduated", hasPTE: true },
  OK: { name: "Oklahoma", topRate: 0.0475, kind: "graduated", hasPTE: true },
  OR: { name: "Oregon", topRate: 0.099, kind: "graduated", hasPTE: true },
  PA: { name: "Pennsylvania", topRate: 0.0307, kind: "flat", hasPTE: false, note: "Flat 3.07% — limited deductions allowed" },
  RI: { name: "Rhode Island", topRate: 0.0599, kind: "graduated", hasPTE: true },
  SC: { name: "South Carolina", topRate: 0.064, kind: "graduated", hasPTE: true },
  SD: { name: "South Dakota", topRate: 0, kind: "none", hasPTE: false, note: "No state income tax" },
  TN: { name: "Tennessee", topRate: 0, kind: "none", hasPTE: false, note: "No state income tax" },
  TX: { name: "Texas", topRate: 0, kind: "none", hasPTE: false, note: "No state income tax (franchise tax may apply to entities)" },
  UT: {
    name: "Utah",
    topRate: 0.0455,
    kind: "flat",
    hasPTE: true,
    stdDeduction: { single: 0, mfj: 0 },
    note: "Flat 4.55%; uses federal taxable income with state-specific adjustments. Taxpayer credit phases out at higher income.",
  },
  VT: { name: "Vermont", topRate: 0.0875, kind: "graduated", hasPTE: false },
  VA: { name: "Virginia", topRate: 0.0575, kind: "graduated", hasPTE: true },
  WA: { name: "Washington", topRate: 0.07, kind: "limited", hasPTE: false, note: "No income tax on wages; 7% capital gains tax above ~$270K" },
  WV: { name: "West Virginia", topRate: 0.0482, kind: "graduated", hasPTE: true },
  WI: { name: "Wisconsin", topRate: 0.0765, kind: "graduated", hasPTE: true },
  WY: { name: "Wyoming", topRate: 0, kind: "none", hasPTE: false, note: "No state income tax" },
  DC: { name: "District of Columbia", topRate: 0.1075, kind: "graduated", hasPTE: false },
};

// ─── Conformity notes per state ─────────────────────────────────────────
// Key federal/state decoupling issues planners need to remember. The list
// is not exhaustive — verify against current state instructions for any
// final return work. Each note is a one-line summary surfaced in the UI
// when the plan's state is selected so the planner knows to enter a state
// adjustment on the entity card.
export const STATE_CONFORMITY_NOTES = {
  CA: [
    "Bonus depreciation: does NOT conform — full addback required",
    "§179: limited to ~$25K (vs. federal $1M+)",
    "QBI deduction: NOT allowed for state purposes",
    "R&D credit: state-specific computation, separate from federal §41",
  ],
  NY: [
    "Bonus depreciation: partially decoupled — verify post-OBBBA conformity",
    "QBI deduction: NOT allowed for state purposes",
    "NYC residents pay additional 3.078–3.876% city tax",
  ],
  NC: [
    "Bonus depreciation: 85% addback required, recovered over 5 years (15% deductible in year 1)",
    "§179: conforms to federal limits",
    "QBI deduction: NOT allowed for state purposes",
  ],
  NJ: [
    "Bonus depreciation: does NOT conform — full addback required (corporate)",
    "§179: limited to $25K for CBT",
    "QBI deduction: NOT allowed for state purposes",
  ],
  PA: [
    "Bonus depreciation: corporate decoupling (CNI tax); personal income tax has its own rules",
    "Limited deductions — many federal items not allowed",
  ],
  CT: [
    "Bonus depreciation: addback required, recovered over 4 years (corporate)",
    "§179: conforms to federal",
  ],
  MA: [
    "Bonus depreciation: does NOT conform — corporate addback required",
    "§179: limited to $25K",
  ],
  WI: [
    "Bonus depreciation: does NOT conform — addback required",
    "§179: conforms to federal",
  ],
  MD: [
    "Bonus depreciation: addback required; recovered over remaining federal life",
    "§179: limited to $25K",
  ],
  TN: [
    "Personal income tax repealed (Hall tax) — no PIT calc needed",
    "Excise tax (F&E) applies at entity level for some structures",
  ],
  TX: [
    "No state income tax on individuals",
    "Franchise tax may apply to LLCs/Corps with >$2.47M revenue",
  ],
  OH: [
    "Personal income tax has graduated brackets through ~3.5%",
    "Commercial Activity Tax (CAT) — gross receipts based — separate from PIT",
  ],
  IL: [
    "Bonus depreciation: federal bonus % allowed; 100% bonus fully decoupled (addback)",
    "Personal Property Replacement Tax adds 1.5% on partnership/S-corp income",
  ],
  IA: [
    "Bonus depreciation: full conformity since TY2021",
    "Flat 3.8% PIT as of 2025",
  ],
  UT: [
    "Generally conforms to federal taxable income (top-down approach)",
    "Taxpayer credit phases out at higher income — effective rate may exceed 4.55%",
  ],
  ID: [
    "Conforms to most federal provisions",
    "Bonus depreciation: conforms",
  ],
};

// Merge conformity notes onto each STATES entry so consumers get one shape.
for (const id of Object.keys(STATE_CONFORMITY_NOTES)) {
  if (STATES[id]) STATES[id].conformityNotes = STATE_CONFORMITY_NOTES[id];
}

export function getStateConformityNotes(stateId) {
  return STATE_CONFORMITY_NOTES[stateId] || [];
}

export function hasConformityIssues(stateId) {
  return (STATE_CONFORMITY_NOTES[stateId] || []).length > 0;
}

export const STATE_OPTIONS = Object.keys(STATES)
  .sort((a, b) => STATES[a].name.localeCompare(STATES[b].name))
  .map((id) => ({ id, ...STATES[id] }));

/**
 * Approximate state tax — uses flat top rate for v1.
 * For UT, CO, ID, AZ, NM the result is materially correct since they're
 * flat or near-flat. For graduated states, this slightly over-estimates
 * for moderate-income clients — flag in UI.
 */
export function computeStateTax({ state, taxableIncome, capitalGains = 0, filingStatus = "single" }) {
  const s = STATES[state];
  if (!s || s.kind === "none") {
    return { state, totalTax: 0, rate: 0, kind: "none", note: s?.note || "No state income tax" };
  }
  if (s.kind === "limited") {
    // WA: 7% on cap gains above threshold
    const tax = Math.max(0, capitalGains - 270000) * s.topRate;
    return { state, totalTax: tax, rate: s.topRate, kind: "limited", note: s.note };
  }
  const baseTax = Math.max(0, taxableIncome) * s.topRate;
  return {
    state,
    totalTax: baseTax,
    rate: s.topRate,
    kind: s.kind,
    note: s.note,
    approximate: s.kind === "graduated",
  };
}

export default { STATES, STATE_OPTIONS, PTE_AVAILABLE, computeStateTax };
